import React, { useState } from 'react';
import { Box, Tooltip } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { renderHelpContent } from './RichTooltipContent';
import { getHelpPreviewText, type HelpValue } from './helpModel';
import { useTooltipHoverLoadingIndicator } from './useTooltipHoverLoadingIndicator';

interface ActionHelpTooltipProps {
  help?: HelpValue;
  disabledHelp?: HelpValue;
  disabled?: boolean;
  placement?: 'bottom' | 'bottom-start' | 'top' | 'top-start' | 'right';
  fullWidth?: boolean;
  wrapperSx?: SxProps<Theme>;
  /** Keeps the tooltip closed even while hovered, e.g. when a nested control shows its own help. */
  suppressed?: boolean;
  children: React.ReactElement;
}

const ActionHelpTooltip: React.FC<ActionHelpTooltipProps> = ({
  help,
  disabledHelp,
  disabled = false,
  placement = 'top',
  fullWidth = false,
  wrapperSx,
  suppressed = false,
  children,
}) => {
  const content = disabled && disabledHelp ? disabledHelp : help;
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const hoverLoading = useTooltipHoverLoadingIndicator({
    active: tooltipOpen,
    enabled: Boolean(content),
  });

  if (!content) {
    return children;
  }

  return (
    <>
      <Tooltip
        title={renderHelpContent(content)}
        placement={placement}
        arrow
        open={tooltipOpen && !suppressed}
        onOpen={() => {
          setTooltipOpen(true);
          hoverLoading.clear();
        }}
        onClose={() => {
          setTooltipOpen(false);
          hoverLoading.clear();
        }}
      >
        <Box
          component="span"
          tabIndex={disabled ? 0 : undefined}
          aria-label={disabled ? getHelpPreviewText(content) ?? undefined : undefined}
          onMouseEnter={hoverLoading.onMouseEnter}
          onMouseMove={hoverLoading.onMouseMove}
          onMouseLeave={hoverLoading.onMouseLeave}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            width: fullWidth ? '100%' : undefined,
            ...wrapperSx,
          }}
        >
          {children}
        </Box>
      </Tooltip>
      {hoverLoading.indicator}
    </>
  );
};

export default ActionHelpTooltip;
